import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import consoleApi, {
  clearConsoleToken,
  getConsoleToken,
  setConsoleToken,
} from "@/lib/consoleApi";

const ConsoleAuthContext = createContext(null);

export function ConsoleAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = getConsoleToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await consoleApi.get("/console/me");
      setUser(data.user);
      return data.user;
    } catch {
      clearConsoleToken();
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password, remember = true) => {
    const { data } = await consoleApi.post("/console/login", { email, password });
    setConsoleToken(data.token, remember);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await consoleApi.post("/console/logout");
    } catch {
      /* ignore */
    } finally {
      clearConsoleToken();
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      login,
      logout,
      refresh: fetchMe,
    }),
    [user, loading, login, logout, fetchMe],
  );

  return <ConsoleAuthContext.Provider value={value}>{children}</ConsoleAuthContext.Provider>;
}

export function useConsoleAuth() {
  const ctx = useContext(ConsoleAuthContext);
  if (!ctx) throw new Error("useConsoleAuth must be used within ConsoleAuthProvider");
  return ctx;
}
